import { AxiosError } from 'axios'
import { FieldPath, SubmitHandler, useForm, UseFormProps, UseFormReturn, FieldValues } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useOptionalToast } from '@/common/ui/Toast/toastContext'
import { isInsufficientBalanceError, openInsufficientBalanceModal } from '@/app/entities/insufficientBalance/model'

type FieldError = {
    field_errors?: Record<string, string>
    detail?: string | unknown
}

export type FormMutationOptions<TFormData extends FieldValues, TResponse, TError extends FieldError> = {
    mutationFn: (data: TFormData) => Promise<TResponse>
    formOptions?: UseFormProps<TFormData>
    onSuccess?: (data: TResponse, formData: TFormData, form: UseFormReturn<TFormData>) => void
    onError?: (error: AxiosError<TError>, formData: TFormData, form: UseFormReturn<TFormData>) => void
    clearOnSuccess?: boolean
    toastOptions?: {
        enabled?: boolean
        successMessage?: string | ((data: TResponse, formData: TFormData) => string)
        errorMessage?: string | ((error: AxiosError<TError>, formData: TFormData) => string)
    }
}

export const useFormMutation = <TFormData extends FieldValues, TResponse, TError extends FieldError>({
    mutationFn,
    formOptions,
    onSuccess,
    onError,
    clearOnSuccess = false,
    toastOptions,
}: FormMutationOptions<TFormData, TResponse, TError>) => {
    const form = useForm<TFormData>(formOptions)
    const { setError, setValue, clearErrors, reset } = form
    const queryClient = useQueryClient()
    const toastContext = useOptionalToast()
    const canShowToasts = toastOptions?.enabled !== false && !!toastContext

    const resolveSuccessMessage = (data: TResponse, formData: TFormData) => {
        if (typeof toastOptions?.successMessage === 'function') {
            return toastOptions.successMessage(data, formData)
        }

        return toastOptions?.successMessage || 'Успешно сохранено'
    }

    const resolveErrorMessage = (error: AxiosError<TError>, formData: TFormData) => {
        if (typeof toastOptions?.errorMessage === 'function') {
            return toastOptions.errorMessage(error, formData)
        }

        if (typeof toastOptions?.errorMessage === 'string') {
            return toastOptions.errorMessage
        }

        const rawMessage = error.response?.data?.detail
        return typeof rawMessage === 'string' ? rawMessage : error.message || 'Произошла ошибка'
    }

    const mutation = useMutation<TResponse, AxiosError<TError>, TFormData>({
        mutationFn,
        onSuccess: (data, variables) => {
            clearErrors()
            if (clearOnSuccess) {
                reset()
            }
            onSuccess?.(data, variables, form)
            if (canShowToasts) {
                const message = resolveSuccessMessage(data, variables)
                toastContext?.showToast({ type: 'success', message })
            }
        },
        onError: (error, variables) => {
            const shouldOpenInsufficientBalanceModal = isInsufficientBalanceError(error)

            if (shouldOpenInsufficientBalanceModal) {
                openInsufficientBalanceModal()
            }

            // Обработка field errors
            const fieldErrors = error.response?.data?.field_errors
            let hasFieldError = false

            if (fieldErrors && typeof fieldErrors === 'object') {
                Object.entries(fieldErrors).forEach(([field, message]) => {
                    setError(field as FieldPath<TFormData>, {
                        type: 'server',
                        message: message as string,
                    })
                    hasFieldError = true
                })
            }

            // Обработка общей ошибки
            if (!hasFieldError) {
                const rawMessage = error.response?.data?.detail
                const message: string =
                    typeof rawMessage === 'string' ? rawMessage : error.message || 'Произошла ошибка'
                setError('root', { type: 'server', message })
            }

            onError?.(error, variables, form)
            if (canShowToasts && !shouldOpenInsufficientBalanceModal) {
                const message = resolveErrorMessage(error, variables)
                toastContext?.showToast({ type: 'error', message })
            }
        },
    })

    // Функция для автоматической обрезки пробелов в строковых полях
    const trimStringFields = <T>(obj: T): T => {
        if (obj === null || obj === undefined) return obj

        if (typeof obj === 'string') {
            return obj.trim() as T
        }

        if (Array.isArray(obj)) {
            return obj.map(trimStringFields) as T
        }

        if (typeof obj === 'object') {
            const trimmedObj = {} as Record<string, unknown>
            for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
                trimmedObj[key] = trimStringFields(value)
            }
            return trimmedObj as T
        }

        return obj
    }

    const onSubmit: SubmitHandler<TFormData> = data => {
        clearErrors()
        // Автоматически обрезаем пробелы во всех строковых полях
        const trimmedData = trimStringFields(data)
        mutation.mutate(trimmedData)
    }

    return {
        form,
        mutation,
        onSubmit,
        queryClient,
        setError,
        setValue,
        register: form.register,
        handleSubmit: form.handleSubmit,
        errors: form.formState.errors,
        isLoading: mutation.isPending,
        isError: mutation.isError,
        isSuccess: mutation.isSuccess,
        data: mutation.data,
        error: mutation.error,
    }
}
